import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  Chip,
  Skeleton,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import Swal from "sweetalert2";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { api } from "../../utils/api";

export default function ServiceWorkflow({ serviceId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [stages, setStages] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const [wf, rl] = await Promise.all([
        api.get(`/workflows/list.php?service_id=${serviceId}`),
        api.get("/roles/list.php"),
      ]);
      setStages(wf.stages || []);
      setIsCustom(!!wf.is_custom);
      setAvailableRoles((rl.roles || []).map((r) => r.name));
      setDirty(false);
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load workflow" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [serviceId]);

  const updateStage = (idx, patch) => {
    const next = stages.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setStages(next);
    setDirty(true);
  };

  const addStage = () => {
    const nextRole = availableRoles.find((r) => r !== "Admin") || "Team";
    const next = [
      ...stages,
      {
        stage_order: stages.length + 1,
        stage_name: `Stage ${stages.length + 1}`,
        role_required: nextRole,
        is_final: 0,
      },
    ];
    setStages(next);
    setDirty(true);
  };

  const removeStage = (idx) => {
    if (stages.length <= 2) {
      setMsg({ type: "warning", text: "Minimum 2 stages required" });
      return;
    }
    const next = stages
      .filter((_, i) => i !== idx)
      .map((s, i) => ({ ...s, stage_order: i + 1 }));
    setStages(next);
    setDirty(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const copy = stages.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    const renum = copy.map((s, i) => ({ ...s, stage_order: i + 1 }));
    setStages(renum);
    setDirty(true);
  };

  const markFinal = (idx) => {
    const next = stages.map((s, i) => ({
      ...s,
      is_final: i === idx ? 1 : 0,
    }));
    setStages(next);
    setDirty(true);
  };

  const save = async () => {
    const cleaned = stages.map((s, i) => ({
      ...s,
      stage_order: i + 1,
      is_final: i === stages.length - 1 ? 1 : 0,
    }));
    setSaving(true);
    try {
      await api.post("/workflows/save.php", {
        service_id: serviceId,
        stages: cleaned,
      });
      setDirty(false);
      setIsCustom(true);
      setStages(cleaned);
      Swal.fire({
        icon: "success",
        title: "Workflow Saved",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Save Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    const c = await Swal.fire({
      icon: "warning",
      title: "Reset to default?",
      text: "This deletes the custom workflow and falls back to the legacy flow.",
      showCancelButton: true,
      confirmButtonText: "Reset",
    });
    if (!c.isConfirmed) return;

    setSaving(true);
    try {
      await api.post("/workflows/reset.php", { service_id: serviceId });
      await load();
      Swal.fire({
        icon: "success",
        title: "Reset",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Reset Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton height={40} />
        <Skeleton height={60} sx={{ mt: 1 }} />
        <Skeleton height={60} sx={{ mt: 1 }} />
        <Skeleton height={60} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountTreeIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Workflow Designer
          </Typography>
          <Chip
            label={isCustom ? "Custom" : "Default"}
            size="small"
            color={isCustom ? "primary" : "default"}
            variant={isCustom ? "filled" : "outlined"}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          {isCustom && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<RestartAltIcon />}
              onClick={resetToDefault}
              disabled={saving}
            >
              Reset
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={save}
            disabled={!dirty || saving}
          >
            {saving ? "Saving..." : "Save Workflow"}
          </Button>
        </Stack>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {!isCustom && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This service uses the default workflow. Drag stages to reorder, then
          save to create a custom flow for this service.
        </Alert>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="wf-stages">
          {(prov) => (
            <Stack
              spacing={1.5}
              ref={prov.innerRef}
              {...prov.droppableProps}
            >
              {stages.map((s, idx) => {
                const isLast = idx === stages.length - 1;
                return (
                  <Draggable
                    key={`stg-${idx}`}
                    draggableId={`stg-${idx}`}
                    index={idx}
                  >
                    {(dp, snap) => (
                      <Paper
                        ref={dp.innerRef}
                        {...dp.draggableProps}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderLeft: 4,
                          borderLeftColor: isLast
                            ? "success.main"
                            : "primary.main",
                          bgcolor: snap.isDragging
                            ? "action.hover"
                            : "background.paper",
                          boxShadow: snap.isDragging ? 4 : 0,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          alignItems={{ sm: "center" }}
                        >
                          <Box
                            {...dp.dragHandleProps}
                            sx={{
                              cursor: "grab",
                              display: "flex",
                              alignItems: "center",
                              color: "text.secondary",
                            }}
                          >
                            <DragIndicatorIcon />
                          </Box>

                          <Box sx={{ minWidth: 40, textAlign: "center" }}>
                            <Chip
                              label={idx + 1}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 800 }}
                            />
                          </Box>

                          <TextField
                            size="small"
                            label="Stage Name"
                            value={s.stage_name}
                            onChange={(e) =>
                              updateStage(idx, { stage_name: e.target.value })
                            }
                            sx={{ minWidth: 160, flex: 1 }}
                          />

                          <TextField
                            size="small"
                            select
                            label="Role Required"
                            value={s.role_required || ""}
                            onChange={(e) =>
                              updateStage(idx, {
                                role_required: e.target.value,
                              })
                            }
                            sx={{ minWidth: 180 }}
                            helperText="Users of this role will be pickable when creating a job"
                          >
                            {availableRoles.map((r) => (
                              <MenuItem key={r} value={r}>
                                {r}
                              </MenuItem>
                            ))}
                          </TextField>

                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={!!s.is_final}
                                onChange={() => markFinal(idx)}
                              />
                            }
                            label="Final"
                            sx={{ m: 0 }}
                          />

                          <Tooltip title="Remove stage">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={stages.length <= 2}
                                onClick={() => removeStage(idx)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Paper>
                    )}
                  </Draggable>
                );
              })}
              {prov.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addStage}>
          Add Stage
        </Button>
        {dirty && (
          <Chip
            label="Unsaved changes"
            color="warning"
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <Alert severity="info" sx={{ mt: 2 }}>
        Drag stages by the handle to reorder. The last stage is automatically
        the final stage. When creating a job, you&apos;ll pick one user per
        stage from the users that have the stage&apos;s role.
      </Alert>
    </Box>
  );
}
